import { zodResolver } from '@hookform/resolvers/zod';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { z } from 'zod';
import { PhoneDataType } from '@aqarmap/aqarmap-ui';
import { Button } from '@aqarmap/aqarmap-ui';
import Modal from '~/components/Modal';
import LayoutSkeleton from '~/components/LayoutSkeleton';
import ErrorMessage from '~/components/ErrorMessage';
import { useAppDispatch } from '~/redux/hooks';
import useFormValidations from '~/hooks/useFormValidations';
import { useGetUserQuery } from '~/redux/api/userApiSlice';
import { useAddPhoneNumberMutation } from '~/redux/api/userApiSlice';
import { closeModal } from '~/redux/features/uiState/uiStateSlice';
import PhoneInputController from '~/components/PhoneInputController';
import { useFullRoutes } from '~/hooks';

const AddContactModal = () => {
  const { MY_ACCOUNT_URL } = useFullRoutes();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { t } = useTranslation('app');
  const [errorMessage, setErrorMessage] = useState('');
  const [phoneData, setPhoneData] = useState<PhoneDataType | null>(null);
  const { isLoading: userIsLoading, isSuccess } = useGetUserQuery();
  const { phoneSchema } = useFormValidations();
  const updatePhoneSchema = z.object({ phone: phoneSchema });
  type IUpdatePhone = z.infer<typeof updatePhoneSchema>;
  const [
    addPhoneNumber,
    { isLoading: addPhoneNumberIsLoading, isError: addPhoneNumberIsError },
  ] = useAddPhoneNumberMutation();

  const { control, handleSubmit } = useForm<IUpdatePhone>({
    resolver: zodResolver(updatePhoneSchema),
    mode: 'onChange',
  });

  const onSubmit = async () => {
    if (phoneData) {
      const formData = {
        number: phoneData.number,
        country_code: phoneData.countryCode,
      };
      const res: any = await addPhoneNumber(formData);
      if (res.data && !res.error) {
        dispatch(closeModal('addPhoneNumber'));
        navigate(MY_ACCOUNT_URL.MANAGE_CONTACTS);
      }
      if (res.error) {
        setErrorMessage(t('failedAddPhoneNumber'));
      }
    }
  };

  return (
    <Modal
      modalKey="addPhoneNumber"
      title={t('addPhoneNumber')}
      bodyClassName="pb-3x"
    >
      {addPhoneNumberIsError && <ErrorMessage message={errorMessage} />}
      {userIsLoading && <LayoutSkeleton />}
      {!userIsLoading && isSuccess && (
        <form
          className="w-full rounded-[12px] "
          onSubmit={handleSubmit(onSubmit)}
          autoComplete="off"
        >
          <div className="mb-3x ">
            <PhoneInputController
              control={control}
              field={{
                placeholder: t('phoneNumber'),
                autoFocus: true,
                name: 'phone',
                autoComplete: 'off',
              }}
              label={{ text: t('phoneNumber'), className: 'font-[600] block' }}
              handleChange={(phoneData) => setPhoneData(phoneData)}
            />
          </div>
          <Button
            size="medium"
            ariaLabel="submit-btn"
            type="submit"
            variant="primary"
            isLoading={addPhoneNumberIsLoading}
            disabled={addPhoneNumberIsLoading}
            className="w-full"
          >
            {t('save')}
          </Button>
        </form>
      )}
    </Modal>
  );
};

export default AddContactModal;
